import React, { useState, useEffect, useRef } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
	ButtonGroup,
	Button,
	Dropdown,
	FormGroup,
	FormLabel,
	FormControl,
} from "react-bootstrap";
import {
	FiPlus,
	FiUpload,
	FiClock,
	FiMoreHorizontal,
	FiList,
	FiSliders,
	FiDownload,
	FiCheckCircle,
	FiPauseCircle,
	FiUserX,
} from "react-icons/fi";
import { FaRegFilePdf } from "react-icons/fa6";
import { MdOutlineTimer } from "react-icons/md";
import { Link } from "react-router-dom";
import { useAttendance } from "../../hooks/attendance/AttendanceContext";
import useAttendanceExport from "../../hooks/attendance/useAttendanceExport";
import Switch from "components/Switch";
import "./Header.scss";
import { useDispatch, useSelector } from "react-redux";
import { showModal, hideModal } from "actions/modalActions";
import { timesService, accountsService, plansService, transactionsService } from "services";
import TimesNavigationTabs from "../Times/TimesNavigationTabs";
import KpiCard from "components/KpiCard";
import AttendanceStatusCard from "./AttendanceStatusCard";

// Importar componentes de filtro
import EmployeeFilter from "./filters/EmployeeFilter";
import GroupFilter from "./filters/GroupFilter";
import DateFilter from "./filters/DateFilter";

// Usar las constantes importadas del componente padre
import { TIME_LIST_VIEWS } from "../../pages/AttendanceSupervisor";
import moment from "moment";
import { formatLocalizedDate } from "utils/dateConfig";
import {
	buildTimeRegistrationReportEntries,
	getPlannedRestDays,
} from "utils/datetime";
import { getAppName, shouldIncludeSupervised } from "utils/utils";
import { createApprovalLock } from "utils/approvalInFlight";
import { trackHaddockEvent, getApprovalActorProps } from "utils/posthogTracking";
import { useTranslation } from "react-i18next";

/**
 * Cabecera para la vista de Supervisor de Asistencia
 * Contiene todos los filtros, controles y acciones principales
 */
const Header = ({ onViewChange, timeListView = TIME_LIST_VIEWS.list }) => {
	const { t } = useTranslation();
	// Obtener el usuario del estado de Redux
	const user = useSelector((state) => state.authReducer.user);

	// Estado local para mostrar/ocultar opciones de filtro
	const [showFilterOptions, setShowFilterOptions] = useState(false);

	// Estado para opciones de exportación
	const [includeApprovedWorkflows, setIncludeApprovedWorkflows] =
		useState(false);

	// Estados para los filtros (equivalentes a los de Times.js)
	const [filter_approved, setFilterApproved] = useState(false);
	const [not_ended, setNotEnded] = useState(false);
	const [show_rest_time, setShowRestTime] = useState(false);
	const [filter_alertas, setFilterAlertas] = useState("-1");

	// Estado para el cambio de vista
	const [isChangingView, setIsChangingView] = useState(false);
	const [isApprovingAll, setIsApprovingAll] = useState(false);
	const approvalTimeoutRef = useRef(null);
	const approvalLockRef = useRef(createApprovalLock());

	// Estado para el resumen de fichajes de hoy (activos/pausa/ausentes)
	const [timeRegistrationsSummary, setTimeRegistrationsSummary] =
		useState(null);

	// Obtener datos del contexto
	const { stats, filters, items, setItems } = useAttendance();
	const { toggleFilter, setFilterAlerts } = filters;

	// Hook de exportación
	const { exportToExcel } = useAttendanceExport();

	const dispatch = useDispatch();

	// Cargar resumen de fichajes de hoy (activos/pausa/ausentes)
	useEffect(() => {
		timesService
			.getSummaryToday()
			.then((res) => setTimeRegistrationsSummary(res))
			.catch(() => setTimeRegistrationsSummary(null));
	}, []);

	useEffect(() => {
		return () => {
			if (approvalTimeoutRef.current) {
				clearTimeout(approvalTimeoutRef.current);
			}
		};
	}, []);

	// Handlers para la UI
	const toggleFilterOptions = () => {
		setShowFilterOptions(!showFilterOptions);
	};

	const handleChangeView = async (view) => {
		// Si ya estamos cambiando de vista, no hacer nada
		if (isChangingView) return;

		// Activar el estado de carga inmediatamente
		setIsChangingView(true);

		// Forzar un re-render antes de la operación asíncrona
		await new Promise((resolve) => setTimeout(resolve, 0));

		try {
			// Utilizamos el handler proporcionado por el padre
			await onViewChange(view);
		} finally {
			// Desactivar el estado de carga después de un breve delay
			// para asegurar que la transición sea suave
			setTimeout(() => {
				setIsChangingView(false);
			}, 500);
		}
	};

	const handleExport = () => {
		exportToExcel({
			employeeIds: filters.employeeIds,
			startDate: moment(filters.dateRange.start).format("YYYY-MM-DD"),
			endDate: moment(filters.dateRange.end).format("YYYY-MM-DD"),
			groupId: filters.groupId,
			includeApprovedWorkflows: includeApprovedWorkflows,
			filterApproved: filter_approved,
			notEnded: not_ended,
			filterAlerts: filter_alertas,
		});
	};

	const handleExportIncludeAllWorkflows = () => {
		exportToExcel({
			employeeIds: filters.employeeIds,
			startDate: moment(filters.dateRange.start).format("YYYY-MM-DD"),
			endDate: moment(filters.dateRange.end).format("YYYY-MM-DD"),
			groupId: filters.groupId,
			includeApprovedWorkflows: includeApprovedWorkflows,
			includeAllWorkflows: true,
			filterApproved: filter_approved,
			notEnded: not_ended,
			filterAlerts: filter_alertas,
		});
	};

	const handleExportTimeRegistrationLog = () => {
		exportToExcel({
			employeeIds: filters.employeeIds,
			startDate: moment(filters.dateRange.start).format("YYYY-MM-DD"),
			endDate: moment(filters.dateRange.end).format("YYYY-MM-DD"),
			groupId: filters.groupId,
			timeRegistrationLog: true,
			filterApproved: filter_approved,
			notEnded: not_ended,
			filterAlerts: filter_alertas,
		});
	};

	const handlePeriodApproval = async () => {
		if (!approvalLockRef.current.tryBegin("period")) return;
		if (isApprovingAll) {
			approvalLockRef.current.end("period");
			return;
		}

		const approvalList = [];
		const validByGroup = {};

		const isValidTimeBound = (value) =>
			isInformed(value) && moment(value, moment.ISO_8601, true).isValid();

		const getGroupKey = (item) => {
			const employeeId = item.employee?.id || item.employee_id || "unknown";
			const dayBase = item.day || item.start;
			const day = isInformed(dayBase)
				? moment(dayBase).format("YYYY-MM-DD")
				: "unknown-day";
			return `${employeeId}_${day}`;
		};

		// 1) Marcar qué grupos (empleado+día) están completos.
		items.forEach((item) => {
			const key = getGroupKey(item);
			const isComplete =
				isValidTimeBound(item.start) && isValidTimeBound(item.end);

			if (validByGroup[key] === undefined) {
				validByGroup[key] = isComplete;
			} else {
				validByGroup[key] = validByGroup[key] && isComplete;
			}
		});

		// 2) Aprobar solo fichajes no aprobados de grupos completamente cerrados.
		items.forEach((item) => {
			const key = getGroupKey(item);
			if (validByGroup[key] && !item.approved) {
				approvalList.push({ id: item.id, approved: true });
			}
		});

		trackHaddockEvent(
			"time_approval__bulk_filtered__clicked__attendance_supervisor",
			{
				...getApprovalActorProps(user),
				surface: "attendance_supervisor",
				action_type: "bulk_click",
				bulk_scope: "filtered_results",
				candidate_records_count: items.length,
				affected_records_count: approvalList.length,
				filter_start_date: moment(filters.dateRange.start).format("YYYY-MM-DD"),
				filter_end_date: moment(filters.dateRange.end).format("YYYY-MM-DD"),
				filter_group_id: filters.groupId || null,
				filter_employee_ids_count: Array.isArray(filters.employeeIds)
					? filters.employeeIds.length
					: 0,
				filter_pending_only: filter_approved,
				filter_not_ended: not_ended,
				filter_alerts: filter_alertas,
				time_list_view: timeListView,
			}
		);

		if (approvalList.length === 0) {
			approvalLockRef.current.end("period");
			dispatch(
				showModal({
					modalProps: {
						open: true,
						title: t('common.attention'),
						style: {
							width: "400px",
						},
						message: t('attendance.noPendingApprove'),
						closeModal: () => dispatch(hideModal()),
					},
					modalType: "alert",
				})
			);
			return;
		}

		setIsApprovingAll(true);

		try {
			// Mitigación de condiciones de carrera: pequeño debounce antes del PATCH masivo.
			await new Promise((resolve) => {
				approvalTimeoutRef.current = setTimeout(resolve, 100);
			});
			approvalTimeoutRef.current = null;

			const response = await timesService.approve(approvalList);

			if (response.ok) {
				// Actualizar el estado local
				const updatedItems = items.map((item) => {
					if (approvalList.some((approval) => approval.id === item.id)) {
						return { ...item, approved: true };
					}
					return item;
				});
				setItems(updatedItems);

				dispatch(
					showModal({
						modalProps: {
							open: true,
							title: t('common.success'),
							style: {
								width: "400px",
							},
							message: t('attendance.approveSuccess'),
							closeModal: () => dispatch(hideModal()),
						},
						modalType: "alert",
					})
				);
			} else {
				throw new Error(
					response.errors?.[0]?.description || t('times.approveErrorTitle')
				);
			}
		} catch (error) {
			console.error("Error al aprobar los fichajes:", error);
			dispatch(
				showModal({
					modalProps: {
						open: true,
						title: "Error",
						message:
							error.message || t('attendance.approveError'),
						closeModal: () => dispatch(hideModal()),
					},
					modalType: "alert",
				})
			);
		} finally {
			approvalLockRef.current.end("period");
			setIsApprovingAll(false);
		}
	};

	// Estado para controlar la carga del reporte
	const [isGeneratingReport, setIsGeneratingReport] = useState(false);

	// Handler para los switches
	const handleSwitch = (id) => {
		switch (id.currentTarget.id) {
			case "show_rest_time":
				setShowRestTime(!show_rest_time);
				toggleFilter("show_rest_time", !show_rest_time);
				break;
			case "not_ended":
				setNotEnded(!not_ended);
				toggleFilter("not_ended", !not_ended ? "1" : "0");
				break;
			case "filter_approved":
				setFilterApproved(!filter_approved);
				toggleFilter("filter_approved");
				break;
			case "includeApprovedWorkflows":
				setIncludeApprovedWorkflows(!includeApprovedWorkflows);
				break;
			default:
				break;
		}
	};

	// dibuja los dos recuadros de firma
	function addSignatureBoxes(doc, mainColor, startY) {
		const marginX = 14,
			gap = 5,
			boxHeight = 15;
		const pageWidth = doc.internal.pageSize.getWidth();
		const pageHeight = doc.internal.pageSize.getHeight();
		const usableWidth = pageWidth - marginX * 2;
		const boxWidth = (usableWidth - gap) / 2;
		let y = startY;
		if (y + boxHeight + 14 > pageHeight) {
			doc.addPage();
			y = 14;
		}
		doc.setLineWidth(0.25);
		doc.setDrawColor(mainColor[0], mainColor[1], mainColor[2]);
		const box1X = marginX,
			box2X = marginX + boxWidth + gap;
		doc.rect(box1X, y, boxWidth, boxHeight);
		doc.rect(box2X, y, boxWidth, boxHeight);
		doc.setFontSize(7.5);
		doc.text(t('times.pdf.workerSignature'), box1X + 2.5, y + 4.5);
		doc.text(t('times.pdf.managerSignature'), box2X + 2.5, y + 4.5);
		return y + boxHeight;
	}

	const handleGenerateReport = async () => {
		if (
			!isInformed(filters.dateRange.start) ||
			!isInformed(filters.dateRange.end)
		) {
			console.warn("Rango de fechas no válido.");
			return;
		}

		try {
			setIsGeneratingReport(true);

			const startDate = moment(filters.dateRange.start).format("YYYY-MM-DD");
			const endDate = moment(filters.dateRange.end).format("YYYY-MM-DD");

			// Preparar parámetros de la API robustos
			const params = {
				page: 0,
				page_size: 10000,
				start_from: startDate,
				start_to: endDate,
				sort: "+employee,+day",
				include_supervised: true,
				group_id: filters.groupId !== "all" ? filters.groupId : "",
				employee_ids:
					filters.employeeIds && filters.employeeIds.length > 0
						? filters.employeeIds
						: "",
			};

			const data = await timesService.getAll(params);
			// Usar items o data
			const times = data.items || data.data || [];
			const employeeIds = [
				...new Set(times.map((time) => time.employee?.id).filter(Boolean)),
			];

			let plannedRestDays = [];
			if (employeeIds.length > 0) {
				try {
					const planResponse = await plansService.getPlanAssignments({
						start: startDate,
						end: endDate,
						employee_ids: employeeIds,
						page_size: 10000,
					});
					plannedRestDays = getPlannedRestDays(planResponse?.data);
				} catch (error) {
					console.warn("No se pudieron obtener los descansos planificados:", error);
				}
			}

			// Obtener horas objetivo para el periodo
			let objectiveByEmployee = {};
			try {
				const accountsResponse = await accountsService.getAll("Horas objetivo");
				const accountObjetivoId =
					accountsResponse?.data?.length > 0
						? accountsResponse.data[0].id
						: null;
				if (accountObjetivoId) {
					const allEmployeeIds = employeeIds.join(",");
					const transResponse = await transactionsService.getAll(
						allEmployeeIds,
						accountObjetivoId,
						startDate,
						endDate,
						"",
						0,
						"+day",
						100000
					);
					if (transResponse?.data) {
						transResponse.data.forEach((tx) => {
							const empId = tx.employee?.id || tx.employee_id;
							if (empId) {
								objectiveByEmployee[empId] =
									(objectiveByEmployee[empId] || 0) + (tx.value || 0);
							}
						});
					}
				}
			} catch (e) {
				console.warn("No se pudieron obtener horas objetivo:", e);
			}

			if (times.length === 0) {
				dispatch(
					showModal({
						modalProps: {
							open: true,
							title: t('common.attention'),
							message:
								t('attendance.reportNoData'),
							closeModal: () => dispatch(hideModal()),
						},
						modalType: "alert",
					})
				);
				return;
			}

			// Helper para formatear horas decimales como "Xh Ymin"
			function formatDecimalHours(decimalHours) {
				const sign = decimalHours < 0 ? "-" : decimalHours > 0 ? "+" : "";
				const abs = Math.abs(decimalHours);
				const hours = Math.floor(abs);
				const minutes = Math.round((abs - hours) * 60);
				if (hours > 0 && minutes > 0) return `${sign}${hours}h ${minutes}min`;
				if (hours > 0) return `${sign}${hours}h`;
				if (minutes > 0) return `${sign}${minutes}min`;
				return "0h";
			}

			// Helper para duración exacta
			function formatDurationExact(startISO, endISO) {
				if (!endISO) return "-";
				const start = moment(startISO);
				const end = moment(endISO);
				const totalSeconds = Math.max(0, Math.floor(end.diff(start) / 1000));
				if (totalSeconds < 60) return `${totalSeconds} s`;
				const hours = Math.floor(totalSeconds / 3600);
				const minutes = Math.floor((totalSeconds % 3600) / 60);
				if (hours > 0 && minutes > 0) return `${hours} h ${minutes} min`;
				if (hours > 0) return `${hours} h`;
				return `${minutes} min`;
			}

			const fechaGeneracion = moment().format("L LT");
			const periodo = `${moment(startDate).format("L")} - ${moment(endDate).format("L")}`;
			const appName = getAppName();
			const mainColor = appName === "Plain" ? [91, 73, 254] : [0, 0, 0];

			// Agrupar por empleado
			const groups = new Map();
			for (const t of times) {
				const emp = t.employee || {};
				const key =
					emp.id ??
					`${emp.name || ""}|${emp.surname || ""}|${emp.nif || ""}|${emp.company?.name || ""}`;
				if (!groups.has(key)) groups.set(key, []);
				groups.get(key).push(t);
			}

			const doc = new jsPDF();
			let first = true;

			for (const [, empTimes] of groups) {
				if (!first) doc.addPage();
				first = false;

				const { name, surname, company, nif } = empTimes[0].employee || {};
				const nombreTrabajador = nif
					? `${name} ${surname} (NIF: ${nif})`
					: `${name} ${surname}`;
				const nombreEmpresa = company?.name || "-";
				const cifEmpresa = company?.vat_number || "-";

				doc.setFontSize(14);
				doc.text(t('times.pdf.title'), 14, 12);

				autoTable(doc, {
					startY: 15,
					body: [
						[t('times.pdf.company'), nombreEmpresa, t('times.pdf.cif'), cifEmpresa],
						[t('times.pdf.worker'), nombreTrabajador, t('times.pdf.period'), periodo],
						[t('times.pdf.generationDate'), fechaGeneracion, t('times.pdf.source'), appName],
					],
					theme: "grid",
					styles: {
						fontSize: 8,
						cellPadding: 1,
						lineColor: [190, 190, 190],
						lineWidth: 0.1,
						valign: "middle",
					},
					tableLineColor: [190, 190, 190],
					tableLineWidth: 0.1,
					margin: { left: 14, right: 14 },
					columnStyles: {
						0: { fontStyle: "bold", fillColor: [245, 245, 245], cellWidth: 32 },
						1: { cellWidth: 59 },
						2: { fontStyle: "bold", fillColor: [245, 245, 245], cellWidth: 32 },
						3: { cellWidth: 59 },
					},
				});

				const yAfterHeader = doc.lastAutoTable.finalY + 6;
				doc.setFontSize(10);
				doc.text(t('times.pdf.timesSubtitle'), 14, yAfterHeader);

				const empId = empTimes[0].employee?.id;
				const { entries: reportEntries, totalRestSeconds } =
					buildTimeRegistrationReportEntries(empTimes, t('times.pdf.weeklyRest'), {
						employeeId: empId,
						plannedRestDays,
					});
				const fichajes = reportEntries.map((entry) => {
					const dia = moment(entry.day, "YYYY-MM-DD").format("L");
					const entrada = entry.start
						? moment(entry.start).local().format("HH:mm")
						: "-";
					const salida = entry.end
						? moment(entry.end).local().format("HH:mm")
						: "-";
					const duracion = entry.kind === "day-rest"
						? "-"
						: entry.end
						? formatDurationExact(entry.start, entry.end)
						: "-";
					return { dia, entrada, salida, tipo: entry.typeName, duracion };
				});

				autoTable(doc, {
					startY: yAfterHeader + 3,
					head: [
						[t('times.pdf.day'), t('times.pdf.entryTime'), t('times.pdf.exitTime'), t('times.pdf.timeType'), t('times.pdf.duration')],
					],
					body: fichajes.map((f) => [f.dia, f.entrada, f.salida, f.tipo, f.duracion]),
					styles: { fontSize: 8, cellPadding: 1, valign: "middle" },
					tableLineColor: mainColor,
					tableLineWidth: 0.1,
					margin: { left: 14, right: 14, bottom: 13 },
					rowPageBreak: "avoid",
					columnStyles: {
						0: { cellWidth: 28 },
						1: { cellWidth: 38 },
						2: { cellWidth: 32 },
						3: { cellWidth: 52 },
						4: { cellWidth: 32 },
					},
					headStyles: {
						fillColor: mainColor,
						textColor: 255,
						halign: "left",
						cellPadding: 1,
					},
					bodyStyles: { halign: "left", cellPadding: 1, lineWidth: 0.1 },
				});

				// Calcular totales de horas trabajadas y objetivo para este empleado
				const totalWorkedSeconds = empTimes.reduce((acc, f) => {
					if (f.start && f.end && f.type?.work_time !== false) {
						return (
							acc +
							Math.max(
								0,
								Math.floor(moment(f.end).diff(moment(f.start)) / 1000)
							)
						);
					}
					return acc;
				}, 0);
				const totalWorkedHours = totalWorkedSeconds / 3600;
				const totalObjectiveHours = empId ? objectiveByEmployee[empId] || 0 : 0;
				const balanceHours = totalWorkedHours - totalObjectiveHours;

				const summaryY = doc.lastAutoTable.finalY + 4;
				autoTable(doc, {
					startY: summaryY,
					theme: "grid",
					head: [[{ content: t('times.pdf.summary'), colSpan: 4 }]],
					body: [
						[
							t('times.balance.targetLabel'),
							totalObjectiveHours > 0
								? formatDecimalHours(totalObjectiveHours).replace(/^[+-]/, "")
								: "-",
							t('times.pdf.workedHours'),
							formatDecimalHours(totalWorkedHours).replace(/^[+-]/, ""),
						],
						[
							t('times.pdf.restTime'),
							formatDecimalHours(totalRestSeconds / 3600).replace(/^[+-]/, ""),
							t('times.table.balance'),
							totalObjectiveHours > 0 ? formatDecimalHours(balanceHours) : "-",
						],
					],
					styles: { fontSize: 8, cellPadding: 1 },
					tableLineColor: mainColor,
					tableLineWidth: 0.1,
					margin: { left: 14, right: 14 },
					pageBreak: "avoid",
					rowPageBreak: "avoid",
					headStyles: {
						fillColor: mainColor,
						textColor: 255,
						halign: "left",
						cellPadding: 1,
					},
					columnStyles: {
						0: { fontStyle: "bold", cellWidth: 58 },
						1: { cellWidth: 33 },
						2: { fontStyle: "bold", cellWidth: 58 },
						3: { cellWidth: 33 },
					},
					bodyStyles: {
						fillColor: [255, 255, 255],
						halign: "left",
						cellPadding: 1,
						lineWidth: 0.1,
					},
					tableWidth: "auto",
				});

				const yAfterTable = doc.lastAutoTable.finalY + 3;
				addSignatureBoxes(doc, mainColor, yAfterTable);
			}

			const pageCount = doc.internal.getNumberOfPages();
			for (let i = 1; i <= pageCount; i++) {
				doc.setPage(i);
				doc.setFontSize(9);
				doc.text(t('times.pdf.page', { current: i, total: pageCount }), 195, 290, { align: "right" });
			}

			const filename =
				groups.size === 1
					? `informe_horario_${[...groups.values()][0][0].employee?.name || "empleado"}.pdf`
					: `informe_horario_varios_empleados.pdf`;
			doc.save(filename);
		} catch (error) {
			console.error("Error generando informe:", error);
			dispatch(
				showModal({
					modalProps: {
						open: true,
						title: "Error",
						message: t('attendance.reportError'),
						closeModal: () => dispatch(hideModal()),
					},
					modalType: "alert",
				})
			);
		} finally {
			setIsGeneratingReport(false);
		}
	};

	const handleChangeSearch = (e) => {
		if (e.target.id === "filter_alertas") {
			setFilterAlertas(e.target.value);
			setFilterAlerts(e.target.value);
		}
	};

	const isInformed = (value) => {
		return (
			value !== undefined && value !== null && value !== "" && value !== "null"
		);
	};

	// Determinar si estamos en vista de lista
	const is_list_view = timeListView === TIME_LIST_VIEWS.list;

	// const showGenerarInforme = isInformed(filters.employeeIds) && isInformed(filters.dateRange.start) && isInformed(filters.dateRange.end);
	const showGenerarInforme =
		isInformed(filters.dateRange.start) && isInformed(filters.dateRange.end);

	return (
		<>
			<div className="heading">
				<div className="heading-left">
					<h1 className="title">{t('times.title')}</h1>
				</div>

				<div className="heading-filters">
					<ButtonGroup
						className="attendance-view-toggle"
						aria-label={t('attendance.listView')}
					>
						<Button
							variant={`${
								TIME_LIST_VIEWS.list === timeListView ? "primary" : "default"
							}`}
							onClick={() =>
								TIME_LIST_VIEWS.list !== timeListView &&
								handleChangeView(TIME_LIST_VIEWS.list)
							}
							disabled={isChangingView}
							className={isChangingView ? "changing-view" : ""}
						>
							{/* <FiList className="icon" /> */}
							{t('times.noGrouping')}
							{isChangingView && TIME_LIST_VIEWS.list === timeListView && (
								<span className="loading-spinner"></span>
							)}
						</Button>
						<Button
							variant={`${
								TIME_LIST_VIEWS.employee_day === timeListView
									? "primary"
									: "default"
							}`}
							onClick={() =>
								TIME_LIST_VIEWS.employee_day !== timeListView &&
								handleChangeView(TIME_LIST_VIEWS.employee_day)
							}
							disabled={isChangingView}
							className={isChangingView ? "changing-view" : ""}
						>
							{/* <MdOutlineTimer className="icon" /> */}
							{t('times.groupByDay')}
							{isChangingView &&
								TIME_LIST_VIEWS.employee_day === timeListView && (
									<span className="loading-spinner"></span>
								)}
						</Button>
					</ButtonGroup>

					<Link
						to={`/times/${user?.id}/employee`}
						title={t('times.registerTimes')}
						className="btn btn-new ml10"
					>
						<FiPlus />
					</Link>

					<Dropdown>
						<Dropdown.Toggle variant="action">
							<FiMoreHorizontal className="icon-actions" />
						</Dropdown.Toggle>
						<Dropdown.Menu>
							{showGenerarInforme && (
								<Dropdown.Item as="button" onClick={handleGenerateReport}>
									<FaRegFilePdf className="dropdown-icon" /> {t('times.dropdown.generateReport')}
								</Dropdown.Item>
							)}

							<Dropdown.Item as={Link} to="/time-registrations/import">
								<FiDownload className="dropdown-icon" /> {t('times.dropdown.importTimes')}
							</Dropdown.Item>

							<Dropdown.Item as="button" onClick={handleExport}>
								<FiUpload className="dropdown-icon" /> {t('times.dropdown.exportExcel')}
							</Dropdown.Item>

							<Dropdown.Item
								as="button"
								onClick={handleExportIncludeAllWorkflows}
							>
								<FiUpload className="dropdown-icon" /> {t('times.dropdown.exportExcelAbsences')}
							</Dropdown.Item>

							<Dropdown.Item
								as="button"
								onClick={handleExportTimeRegistrationLog}
							>
								<FiUpload className="dropdown-icon" /> {t('times.dropdown.exportExcelAudit')}
							</Dropdown.Item>

							<FormGroup
								className="form-group form-switch export-config"
								controlId="includeApprovedWorkflows"
							>
								<Switch
									controlId="includeApprovedWorkflows"
									isOn={includeApprovedWorkflows}
									handleToggle={handleSwitch}
								/>
								<FormLabel className="export-config-label">
									{t('times.dropdown.includeAbsences')}
								</FormLabel>
							</FormGroup>
						</Dropdown.Menu>
					</Dropdown>
				</div>
			</div>

			<TimesNavigationTabs />

			<div className="subheading">
				<div className="kpi-cards-container">
					<AttendanceStatusCard
						summary={timeRegistrationsSummary}
						iconColor="#0BCC8A"
					/>
					<KpiCard
						className="worked"
						iconColor="#0BCC8A"
						title={t('attendance.totalWorked')}
						value={stats?.totalWorking || "0h 00m 00s"}
						icon={<MdOutlineTimer />}
					/>
				</div>

				<div className="subheading-filters">
					<div className="attendance-filters-fields">
						<DateFilter />
						<EmployeeFilter />
						<GroupFilter />
					</div>
					<div className="attendance-filters-actions">
						<Dropdown
							className="dropdown-filter attendance-settings-dropdown"
							show={showFilterOptions}
						>
							<Dropdown.Toggle
								variant="action"
								onClick={() => toggleFilterOptions()}
								aria-label={t('times.moreFilters')}
							>
								<FiSliders className="icono-filtro" title={t('times.moreFilters')} />
							</Dropdown.Toggle>
							<Dropdown.Menu>
								<Dropdown.Header>{t('times.moreFilters')}</Dropdown.Header>

								<div className="dropdown-content">
									{timeListView === TIME_LIST_VIEWS.employee_day && (
										<FormGroup
											className="form-group form-group-flex form-switch"
											controlId="show_rest_time"
										>
											<Switch
												controlId="show_rest_time"
												isOn={show_rest_time}
												handleToggle={handleSwitch}
											/>
											<FormLabel
												title={t('times.filter.showRestTitle')}
												className="mb-1"
											>
												{t('attendance.restFilter')}
											</FormLabel>
										</FormGroup>
									)}
									<FormGroup
										className="form-group form-group-flex form-switch"
										controlId="not_ended"
									>
										<Switch
											controlId="not_ended"
											isOn={not_ended}
											handleToggle={handleSwitch}
										/>
										<FormLabel
											title={t('times.filter.showOpenTitle')}
											className="mb-1"
										>
											{t('times.filter.showOpen')}
										</FormLabel>
									</FormGroup>

									<FormGroup
										className="form-group form-group-flex form-switch"
										controlId="filter_approved"
									>
										<Switch
											controlId="filter_approved"
											isOn={filter_approved}
											handleToggle={handleSwitch}
										/>
										<FormLabel
											title={t('times.filter.showPendingTitle')}
											className="mb-1"
										>
											{t('times.filter.showPending')}
										</FormLabel>
									</FormGroup>

									<FormGroup
										className="form-group form-group-side"
										controlId="filter_alertas"
									>
										<FormLabel>{t('times.filter.alerts')}</FormLabel>
										<FormControl
											type="text"
											value={filter_alertas}
											placeholder={t('times.filter.alerts')}
											onChange={handleChangeSearch}
											as="select"
										>
											<option value="-1">{t('times.filter.showAll')}</option>
											<option value="0">{t('times.filter.showNoAlerts')}</option>
											<option value="1">{t('times.filter.showWithAlerts')}</option>
										</FormControl>
									</FormGroup>
								</div>

								<div className="dropdown-footer">
									<button
										type="button"
										className="btn btn-outline-primary"
										onClick={() => toggleFilterOptions()}
									>
										{t('common.close')}
									</button>
								</div>
							</Dropdown.Menu>
						</Dropdown>
						<Button
							variant="primary"
							className="attendance-approve-btn"
							onClick={handlePeriodApproval}
							disabled={isApprovingAll}
						>
							{isApprovingAll ? t('attendance.approvingAll') : t('attendance.approveAll')}
						</Button>
					</div>
				</div>
			</div>
		</>
	);
};

export default Header;
